// The contract between the Cloud Functions and the browser (docs/decisions.md, "Phase 6 contracts").
// The web app imports this file directly (src/account/ → ../../functions/src/contract.ts), so it must stay
// dependency-free: types and `as const` values only, no imports.

/** Region of the Cloud Functions (and of Firestore). */
export const FUNCTIONS_REGION = 'asia-northeast3' as const;

/** Names of the callable functions. */
export const CALLABLE = {
  createOrder: 'createOrder',
  confirmPayment: 'confirmPayment',
  failOrder: 'failOrder',
  cancelPayment: 'cancelPayment',
  reconcileOrder: 'reconcileOrder',
  requestAccountDeletion: 'requestAccountDeletion',
  signInWithProvider: 'signInWithProvider',
  beginEmailAction: 'beginEmailAction',
  inspectEmailAction: 'inspectEmailAction',
  confirmEmail: 'confirmEmail',
  preparePaymentResume: 'preparePaymentResume',
  adminLookupOrder: 'adminLookupOrder',
  adminLookupAccount: 'adminLookupAccount',
  transferEntitlement: 'transferEntitlement',
  unlinkProvider: 'unlinkProvider',
  listSignInMethods: 'listSignInMethods',
  beginGoogleLink: 'beginGoogleLink',
  approveGoogleSignIn: 'approveGoogleSignIn',
  completeEmailSignIn: 'completeEmailSignIn',
  getEntitlement: 'getEntitlement',
} as const;
export type CallableName = (typeof CALLABLE)[keyof typeof CALLABLE];

/**
 * Ways to sign in. Google and email (a passwordless link Firebase sends) are native Firebase Auth providers; Kakao,
 * Naver and Toss go through the sign-in broker (signInWithProvider), which signs the browser in with a custom token.
 */
export const PROVIDER = { google: 'google', email: 'email', kakao: 'kakao', naver: 'naver', toss: 'toss' } as const;
export type Provider = (typeof PROVIDER)[keyof typeof PROVIDER];
export type BrokeredProvider = Exclude<Provider, 'google' | 'email'>;
/** The providers of the sign-in broker; Toss Login since 2026-10-14 (docs/plans/toss-login.md). */
export const BROKERED_PROVIDERS: readonly BrokeredProvider[] = ['kakao', 'naver', 'toss'];

export type PageLang = 'en' | 'ko';

/**
 * Where the browser goes once a sign-in or an emailed link completes: a page of this site in one language, never an
 * arbitrary URL. `resumeId` is an opaque handle on a payment waiting for its buyer to sign in (never the payment's
 * details); `pendingId` names an emailed link this browser keeps while the right account signs in.
 */
export type ReturnTarget =
  | { route: 'tool' | 'purchase' | 'account' | 'admin'; lang: PageLang }
  | { route: 'purchase-success'; lang: PageLang; resumeId: string }
  | { route: 'email-action'; lang: PageLang; pendingId: string };
export type ReturnRoute = ReturnTarget['route'];

/** signin: sign in or sign up. link: add the provider to the signed-in account. reauth: prove the account again. */
export type SignInIntent = 'signin' | 'link' | 'reauth';

/** The one product: "Remove Ads", sold since the 2026-12 launch for a fixed period (REMOVE_ADS_OFFER). */
export const PRODUCT = { removeAds: 'remove_ads' } as const;
export type ProductId = (typeof PRODUCT)[keyof typeof PRODUCT];

/**
 * The Remove Ads offer of the launch (docs/decisions.md, "Remove Ads — 364 days"): one payment removes the ads for 364
 * days from the payment's approval, while the buyer is signed in to the account that bought it. Toss Payments allows a
 * service period shorter than 12 months (the owner, 2026-11-04), so the period is counted in exact days, not months: Korea
 * has no daylight saving time, so it ends at the same time of day in Korea, 364 days later. It never renews and nothing
 * is charged again; another payment after it ended starts a new period from that payment's approval. Orders and
 * purchases freeze these values (OrderDoc, PurchaseDoc), so a later offer never changes an earlier purchase.
 */
export const REMOVE_ADS_OFFER = {
  product: PRODUCT.removeAds,
  productVersion: 'remove_ads_364d_v1',
  durationDays: 364,
  termTimeZone: 'Asia/Seoul',
} as const;

/** The shape version of the records the 364-day offer writes (orders, purchases, users/{uid}.entitlements). */
export const RECORD_SCHEMA_VERSION = 2;

const DAY_MS = 86_400_000;

/** When a period that starts at `startsAtMs` ends (exclusive): 364 days later, the same time of day in Korea. */
export function termEnd(startsAtMs: number, durationDays: number = REMOVE_ADS_OFFER.durationDays): number {
  return startsAtMs + durationDays * DAY_MS;
}

/** A Firestore Timestamp (Admin or web SDK) as milliseconds, or null for anything else. */
export function millisOf(value: unknown): number | null {
  if (typeof value !== 'object' || value === null) return null;
  const toMillis = (value as { toMillis?: unknown }).toMillis;
  if (typeof toMillis !== 'function') return null;
  const millis: unknown = (toMillis as () => unknown).call(value);
  return typeof millis === 'number' && Number.isFinite(millis) ? millis : null;
}

/** A period of Remove Ads: from `since` (inclusive) to `until` (exclusive), in milliseconds, from one order. */
export interface Grant {
  since: number;
  until: number;
  orderId: string;
}

/**
 * The period users/{uid}.entitlements grants, if it has the current shape: schema 2, `adFree`, both times and the
 * order. Anything else, a legacy `adFree: true` without an end above all, grants nothing: a missing or malformed end
 * never means unlimited.
 */
export function entitlementGrant(entitlements: unknown): Grant | null {
  if (typeof entitlements !== 'object' || entitlements === null) return null;
  const value = entitlements as Partial<Record<keyof Entitlements, unknown>>;
  if (value.schemaVersion !== RECORD_SCHEMA_VERSION || value.adFree !== true) return null;
  const since = millisOf(value.adFreeSince);
  const until = millisOf(value.adFreeUntil);
  const orderId = value.adFreeOrderId;
  if (since === null || until === null || since >= until || typeof orderId !== 'string' || orderId === '') return null;
  return { since, until, orderId };
}

/**
 * The one test of whether Remove Ads applies at `nowMs` (the server's clock on the server; the browser's, set by the
 * server's time, in the browser): a current-shape grant with `since <= now < until`. At `until` exactly it has ended.
 */
export function entitlementActive(entitlements: unknown, nowMs: number): boolean {
  const grant = entitlementGrant(entitlements);
  return grant !== null && grant.since <= nowMs && nowMs < grant.until;
}

/** The purchase's statuses that keep its period (a partial refund keeps it; a full refund ends it). */
export const GRANTING_PURCHASE_STATUSES: readonly string[] = ['DONE', 'PARTIAL_CANCELED'];

/**
 * The period a purchase grants, if it is one of the 364-day offer (schema 2, both times), paid and not fully refunded.
 * A purchase of the earlier offer (no end) grants nothing here: see docs/deploy.md, "Remove Ads — 364 days", for what
 * is checked before the release that brings this rule.
 */
export function purchaseGrant(purchase: unknown, orderId: string): Grant | null {
  if (typeof purchase !== 'object' || purchase === null) return null;
  const value = purchase as Partial<Record<keyof PurchaseDoc, unknown>>;
  if (value.schemaVersion !== RECORD_SCHEMA_VERSION || !GRANTING_PURCHASE_STATUSES.includes(String(value.status))) return null;
  const since = millisOf(value.startsAt);
  const until = millisOf(value.expiresAt);
  if (since === null || until === null || since >= until) return null;
  return { since, until, orderId };
}

/**
 * The currencies Remove Ads is sold in, the buyer's choice. Toss Payments gives each currency its own MID (merchant id)
 * and keys: won are paid with Korean cards and easy-pay services, US dollars with PayPal or cards issued abroad.
 * Amounts are in Toss's units: whole won, or dollars with at most 2 decimals.
 */
export const CURRENCY = { krw: 'KRW', usd: 'USD' } as const;
export type Currency = (typeof CURRENCY)[keyof typeof CURRENCY];
export const CURRENCIES: readonly Currency[] = Object.values(CURRENCY);

/**
 * The dollar amount of the PayPal TEST checkout (docs/deploy.md, "PayPal test checkout"): a fixture for trying Toss's
 * PayPal with test keys and PayPal Sandbox accounts, which both the page and the functions use there. It equals the
 * intended dollar price of the 364-day offer (site.config.json product.removeAds.price.USD, 2.49), so the test exercises
 * two decimals; it is still a test amount on test keys, and says nothing about whether dollars are sold for real
 * (that takes Toss's dollar MID and REMOVE_ADS_PRICE_USD_CENTS=249).
 */
export const PAYPAL_TEST_PRICE_USD = 2.49;

export const ORDER_STATUS = {
  created: 'CREATED',
  paid: 'PAID',
  failed: 'FAILED',
  canceled: 'CANCELED',
} as const;
export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

/** A purchase mirrors the Toss payment's status once it has been approved. */
export const PURCHASE_STATUS = {
  done: 'DONE',
  canceled: 'CANCELED',
  partialCanceled: 'PARTIAL_CANCELED',
} as const;
export type PurchaseStatus = (typeof PURCHASE_STATUS)[keyof typeof PURCHASE_STATUS];

export const COLLECTION = {
  users: 'users',
  orders: 'orders',
  purchases: 'purchases',
  webhookEvents: 'webhookEvents',
  accountDeletions: 'accountDeletions',
  accountDeletionJobs: 'accountDeletionJobs',
  config: 'config',
  identities: 'identities',
  emailActions: 'emailActions',
  paymentResumes: 'paymentResumes',
  adminActions: 'adminActions',
  accountSessions: 'accountSessions',
  accountCredentials: 'accountCredentials',
  identitySessions: 'identitySessions',
  disconnectNotices: 'disconnectNotices',
  providerDisconnectJobs: 'providerDisconnectJobs',
  emailTransitions: 'emailTransitions',
} as const;

/**
 * Codes in `HttpsError.details.code` that come from this app rather than from Toss Payments. Any other code in
 * `details.code` is a Toss Payments error code (https://docs.tosspayments.com/reference/error-codes), and
 * `details.message` is Toss's message for it (Korean), suitable for showing to the buyer.
 */
export const APP_ERROR = {
  termsNotAccepted: 'TERMS_NOT_ACCEPTED',
  ageNotConfirmed: 'AGE_NOT_CONFIRMED',
  alreadyAdFree: 'ALREADY_AD_FREE',
  currencyUnavailable: 'CURRENCY_UNAVAILABLE',
  orderNotFound: 'ORDER_NOT_FOUND',
  notOrderOwner: 'NOT_ORDER_OWNER',
  amountMismatch: 'AMOUNT_MISMATCH',
  orderNotPayable: 'ORDER_NOT_PAYABLE',
  paymentNotDone: 'PAYMENT_NOT_DONE',
  paymentProviderUnavailable: 'PAYMENT_PROVIDER_UNAVAILABLE',
  /** Another payment of this account is being approved: try again in a few minutes. */
  checkoutInProgress: 'CHECKOUT_IN_PROGRESS',
  /** The account is being deleted, or was deleted while its payment was being approved. */
  accountDeleted: 'ACCOUNT_DELETED',
  /** This sign-in method is not configured (or not offered yet). */
  providerUnavailable: 'PROVIDER_UNAVAILABLE',
  /** The provider refused the code (expired, used already, wrong redirect URI): start the sign-in again. */
  providerDenied: 'PROVIDER_DENIED',
  /** The provider could not be reached: start the sign-in again (its code may have been used up). */
  providerUnreachable: 'PROVIDER_UNREACHABLE',
  /** Linking: this Kakao/Naver account belongs to another account of this site. Nothing was moved. */
  identityInUse: 'IDENTITY_IN_USE',
  /** Linking: the account already has another account of this provider. */
  providerAlreadyLinked: 'PROVIDER_ALREADY_LINKED',
  /** The sign-in this account maps to no longer exists or is disabled: it is not recreated. */
  accountUnavailable: 'ACCOUNT_UNAVAILABLE',
  /** The signed-in account changed while an account-bound operation was pending, or it isn't the one it was for. */
  accountMismatch: 'ACCOUNT_MISMATCH',
  /** Linking, unlinking, changing the email and admin changes need a sign-in within the last few minutes. */
  recentSignInRequired: 'RECENT_SIGN_IN_REQUIRED',
  /** The account has no confirmed email (the receipt and recovery address) yet, or it changed since. */
  emailNotConfirmed: 'EMAIL_NOT_CONFIRMED',
  /** The email action is unknown, expired, or a newer one replaced it: request a new link. */
  emailActionExpired: 'EMAIL_ACTION_EXPIRED',
  /** Too many requests in a short time (a new link within a minute of the last). */
  tooManyRequests: 'TOO_MANY_REQUESTS',
  /** The payment waiting for its buyer to sign in is unknown or expired. */
  paymentResumeExpired: 'PAYMENT_RESUME_EXPIRED',
  /** Admin transfer: no such account, or it is being deleted. */
  targetNotFound: 'TARGET_NOT_FOUND',
  /** Admin transfer: the account has no confirmed email (its owner confirms one first). */
  targetNotConfirmed: 'TARGET_NOT_CONFIRMED',
  /** Admin transfer: the order's purchase is refunded or was never approved. */
  purchaseNotActive: 'PURCHASE_NOT_ACTIVE',
  /** Admin transfer: the order changed hands since it was looked up: look it up again. */
  ownershipChanged: 'OWNERSHIP_CHANGED',
  /** Admin transfer: this operation id was used for another request. */
  operationConflict: 'OPERATION_CONFLICT',
  /** Removing a sign-in method needs the confirmed email as the account's way back in. */
  identityRequired: 'IDENTITY_REQUIRED',
  /**
   * This provider account's connection with the site is still being removed at the provider (after it was removed
   * from an account here, or its account was deleted): it can't sign in or be linked until that is done.
   */
  providerRemovalPending: 'PROVIDER_REMOVAL_PENDING',
  /**
   * The person disconnected the site from this provider account moments ago (Toss's notice): it can sign in, be linked
   * or sign in again only once a code issued before that has expired (about 15 minutes).
   */
  providerRecentlyDisconnected: 'PROVIDER_RECENTLY_DISCONNECTED',
  /**
   * The server doesn't redeem email links (AUTH_WEB_API_KEY isn't set): the browser redeems the link itself, as
   * before. Nothing was used up.
   */
  emailLinkInBrowser: 'EMAIL_LINK_IN_BROWSER',
  /** The address isn't the one the link was sent to (the link is still good). */
  emailMismatch: 'EMAIL_MISMATCH',
  /** The address isn't the email the server approved for its account, which signs in only with approved methods. */
  emailNotApproved: 'EMAIL_NOT_APPROVED',
  /** The address is another account's email. */
  emailInUse: 'EMAIL_IN_USE',
  /** The account has an email sign-in with another address already. */
  accountHasOtherEmail: 'ACCOUNT_HAS_OTHER_EMAIL',
  /** The Google account's approval is unknown, used or expired (10 minutes), or wasn't begun here: link it again. */
  googleLinkExpired: 'GOOGLE_LINK_EXPIRED',
  /**
   * New purchases of Remove Ads aren't taken now: before the launch time, or while sales are paused. Nothing was
   * charged. A payment that was already approved is still recorded, refunded and reconciled as before.
   */
  salesClosed: 'SALES_CLOSED',
  /**
   * The offer or the policies the page showed (product version, terms, refund policy) aren't the current ones: reload
   * the page and review them. Nothing was charged.
   */
  offerChanged: 'OFFER_CHANGED',
  /** The order's checkout window (CHECKOUT_WINDOW_MS) is over: start the purchase again. Nothing was charged. */
  checkoutExpired: 'CHECKOUT_EXPIRED',
} as const;
export type AppErrorCode = (typeof APP_ERROR)[keyof typeof APP_ERROR];

/** `details` of an HttpsError thrown by the callables (read it from FunctionsError.details in the browser). */
export interface ErrorDetails {
  code: string;
  message: string;
}

// ---- Callable requests and responses ----

export interface CreateOrderRequest {
  /** The buyer ticked the 이용약관 / refund-policy checkbox. */
  termsAccepted: true;
  /** The buyer confirmed being 14 or older (plan §12). */
  ageConfirmed: true;
  /** The currency the buyer chose to pay in (KRW when absent). */
  currency: Currency;
  /**
   * What the page showed when the buyer agreed: the offer (REMOVE_ADS_OFFER.productVersion) and the versions of the
   * terms and the refund policy (site.config.json legal.terms.version, legal.refund.version). Anything but the current
   * ones is refused with OFFER_CHANGED: the buyer reloads and reviews them, and nothing is applied in their place.
   */
  productVersion: string;
  termsVersion: string;
  refundPolicyVersion: string;
}
export interface CreateOrderResponse {
  orderId: string;
  /** In `currency` (won with VAT, or dollars); always the server's price. */
  amount: number;
  currency: Currency;
  orderName: string;
  /** Random, opaque Toss customerKey stored on users/{uid} (never the email). */
  customerKey: string;
  /** The account's confirmed email, frozen on the order: give it to Toss as customerEmail (the receipt goes there). */
  receiptEmail: string;
  /** The offer frozen on the order (absent from functions deployed before the 364-day offer). */
  productVersion?: string;
  /** Days of Remove Ads from the payment's approval. */
  durationDays?: number;
  termTimeZone?: string;
  termsVersion?: string;
  refundPolicyVersion?: string;
  /** ISO 8601: the order can be paid until then (the checkout window, not the end of the period). */
  checkoutExpiresAt?: string;
}

/** The query parameters Toss appends to the successUrl (`amount` parsed to a number, e.g. 6600 or 4.99). */
export interface PaymentDetails {
  paymentKey: string;
  orderId: string;
  amount: number;
}
/** Toss's parameters, or the handle preparePaymentResume gave for them while the buyer signed in. */
export type ConfirmPaymentRequest = PaymentDetails | { resumeId: string };
export interface ConfirmPaymentResponse {
  orderId: string;
  status: 'PAID';
  /** Toss's payment method label, e.g. "카드", "간편결제" or "해외간편결제". */
  method: string | null;
  /**
   * The easy-pay service, as Toss names it (e.g. "토스페이", "PAYPAL" or "페이팔"); null for a card. Absent from
   * functions deployed before 2026-11.
   */
  provider?: string | null;
  /** The order's currency. Absent from functions deployed before 2026-11. */
  currency?: Currency;
  /** ISO 8601. */
  approvedAt: string;
  /** Toss receipt page (receipt.url). */
  receiptUrl: string | null;
  /** Where Toss sends the receipt: the address frozen on the order (null once it was removed with its account). */
  receiptEmail: string | null;
  /** ISO 8601: the period of Remove Ads this payment bought, from its approval; absent for an earlier offer's. */
  startsAt?: string | null;
  /** ISO 8601, exclusive: the period ends then. */
  expiresAt?: string | null;
  productVersion?: string | null;
  durationDays?: number | null;
  /** ISO 8601: the server's clock when it answered (the browser counts the period by it). */
  serverTime?: string;
}

/**
 * The caller's Remove Ads as the server has it now, with the server's clock: the browser counts the period by this
 * time rather than its own (getEntitlement). Reads only; refused while the account is being deleted.
 */
export type GetEntitlementRequest = Record<string, never>;
export interface GetEntitlementResponse {
  /** ISO 8601: the server's clock when it answered. */
  serverTime: string;
  /** The period recorded for the account (users/{uid}.entitlements), ended or not; null when there is none. */
  entitlement: {
    /** ISO 8601. */
    startsAt: string;
    /** ISO 8601, exclusive. */
    expiresAt: string;
    orderId: string;
    /** It applies at serverTime. */
    active: boolean;
  } | null;
}

/** The query parameters Toss appends to the failUrl. */
export interface FailOrderRequest {
  orderId: string;
  code: string;
  message: string;
}
export interface FailOrderResponse {
  orderId: string;
  status: 'FAILED';
}

/** Admin only (custom claim `admin: true`): full refund. */
export interface CancelPaymentRequest {
  orderId: string;
  reason: string;
}
export interface CancelPaymentResponse {
  orderId: string;
  status: 'CANCELED';
}

/**
 * Admin only: asks Toss for the order's payment and brings the order, purchase and entitlement in line with it (after
 * fixing a secret key, for events Toss stopped resending).
 */
export interface ReconcileOrderRequest {
  orderId: string;
}
export interface ReconcileOrderResponse {
  orderId: string;
  /** The order's status afterwards. */
  status: OrderStatus;
  /** What reconciling did: paid, canceled, stale, unknown_payment, … */
  result: string;
}

// ---- Admin (custom claim `admin: true`): look-ups and moving Remove Ads to another account (SSO plan §7) ----

export type AdminLookupOrderRequest = { orderId: string } | { receiptEmail: string };
/** One step of an order's admin history (adminActions), without raw uids or addresses. */
export interface AdminHistoryEntry {
  /** ISO 8601. */
  at: string;
  action: 'transfer' | 'cancel';
  outcome: string;
  fromUidHash: string | null;
  toUidHash: string | null;
  resultingVersion: number | null;
}
export interface OrderLookup {
  orderId: string;
  /** The account that has Remove Ads from this order now, while it exists. */
  ownerUid: string | null;
  /** Its uid hash (also after the account was deleted). */
  ownerUidHash: string | null;
  ownershipVersion: number;
  status: OrderStatus;
  /** The purchase's status, once Toss approved the payment. */
  purchaseStatus: PurchaseStatus | null;
  amount: number;
  currency: Currency;
  /** ISO 8601. */
  createdAt: string;
  approvedAt: string | null;
  /** The offer the order was made under: REMOVE_ADS_OFFER.productVersion, or null for an order of the earlier offer. */
  productVersion: string | null;
  /** ISO 8601: the period its purchase grants (null before approval, or for the earlier offer). */
  startsAt: string | null;
  expiresAt: string | null;
  /** The period applies now (by the server's clock), and the purchase isn't refunded. */
  periodActive: boolean;
  /** 'test' or 'live': the Toss keys the order was made for (null for an order of the earlier offer). */
  paymentEnvironment: PaymentEnvironment | null;
  /** The receipt email frozen on the order, while it is kept. */
  receiptEmail: string | null;
  /** The receipt email was removed with its account (only its hash is kept). */
  receiptRedacted: boolean;
  /** Searched by address: the order's receipt email, or its hash, is that address. */
  receiptMatches: boolean | null;
  receiptUrl: string | null;
  history: AdminHistoryEntry[];
}
export interface AdminLookupOrderResponse {
  matches: OrderLookup[];
  /** More than the 20 shown matched. */
  hasMore: boolean;
}

export type AdminLookupAccountRequest = { uid: string } | { email: string };
export interface AdminLookupAccountResponse {
  uid: string;
  /** The confirmed email, when the account's email sign-in and profile agree on it. */
  confirmedEmail: string | null;
  /** Remove Ads applies to the account now (entitlementActive, by the server's clock). */
  adFree: boolean;
  /** ISO 8601: the end of the account's recorded period, ended or not; null when it has none. */
  adFreeUntil: string | null;
  disabled: boolean;
  /** Its deletion has started. */
  deleting: boolean;
  /** A payment of the account is being approved right now. */
  checkoutInProgress: boolean;
}

export interface TransferEntitlementRequest {
  /** A random id for this confirmed transfer: a retry with it returns the first result. */
  operationId: string;
  orderId: string;
  /** The order's ownershipVersion as looked up: a transfer of an order that changed since is refused. */
  expectedOwnershipVersion: number;
  to: { uid: string } | { email: string };
  /** Why (support case, proof checked). Only its length is kept. */
  reason: string;
}
export interface TransferEntitlementResponse {
  orderId: string;
  toUid?: string;
  ownershipVersion: number;
  status: 'TRANSFERRED';
}

export type RequestAccountDeletionRequest = Record<string, never>;
export interface RequestAccountDeletionResponse {
  /**
   * requested: the deletion is recorded and the server finishes it by itself, also if this browser goes away or its
   * sign-in expires; completed: every step finished.
   */
  status: 'requested' | 'completed';
}

/**
 * The caller's sign-in methods as the server knows them now: Google and the email from Firebase Auth, Kakao and Naver
 * from the sign-in mappings (identities/), never from the profile. Refused while the account is being deleted.
 */
export type ListSignInMethodsRequest = Record<string, never>;
export interface ListSignInMethodsResponse {
  methods: Provider[];
  /** Linked methods whose person disconnected this site at the provider: signing in with them asks for consent again. */
  disconnected: BrokeredProvider[];
}

/** The provider redirected back to /auth/callback with this code (docs/plans/SSO-login.md §5). */
export interface SignInWithProviderRequest {
  provider: BrokeredProvider;
  /** The provider's authorization code. */
  code: string;
  /** The exact redirect URI the authorization used (checked against an allowlist). */
  redirectUri: string;
  /** Echoed by the provider; Naver needs it in the token call. */
  state: string;
  intent: SignInIntent;
  /** link and reauth: the account that started them, which must be the caller. */
  expectedUid?: string;
}
/** Removes a Kakao, Naver or Toss account from the caller's account (the confirmed email stays as the way back in). */
/** Kakao, Naver or Toss, or the account's Google account (the email is never removed). */
export type RemovableProvider = BrokeredProvider | 'google';
export interface UnlinkProviderRequest {
  provider: RemovableProvider;
}
/**
 * Before linking Google in the browser: a session that counts and signed in recently begins the approval of the Google
 * account it is about to link (credentials.ts). `nonce` goes with the approval, from this browser only; null when the
 * account needs none (it has no credential record).
 */
export type BeginGoogleLinkRequest = Record<string, never>;
export interface BeginGoogleLinkResponse {
  nonce: string | null;
  /**
   * The account's last removal of Google (milliseconds; the record's `googleValidAfter`), or null: a Google sign-in
   * counts only if its auth_time (whole seconds) is after it. The answer comes once that second is over, so a link the
   * browser made within it is signed in again then, and that sign-in counts (linking.ts).
   */
  googleValidAfter: number | null;
}
/**
 * After linking Google (Firebase's linkWithPopup, which makes this session a sign-in of that Google account): the
 * server approves that Google account, with the nonce of the begin. `googleUid` is the Google account just linked (its
 * provider uid), which must be the account's Google now.
 */
export interface ApproveGoogleSignInRequest {
  googleUid: string;
  nonce: string | null;
}
export interface ApproveGoogleSignInResponse {
  approved: true;
}

/**
 * "Continue with email", when the link is opened: the server redeems it (Firebase's oobCode, for this address) and
 * answers with a custom token of the account it signs in to, created if new. Without AUTH_WEB_API_KEY it answers
 * EMAIL_LINK_IN_BROWSER before using anything, and the browser redeems the link itself.
 */
export interface CompleteEmailSignInRequest {
  email: string;
  oobCode: string;
}
export interface CompleteEmailSignInResponse {
  /** Sign the browser in with it (signInWithCustomToken). */
  customToken: string;
  uid: string;
  isNewUser: boolean;
  /** The address, canonical: the account's confirmed email now. */
  email: string;
}

export interface UnlinkProviderResponse {
  provider: RemovableProvider;
  /** Something was linked and is removed now (false: it was not linked). */
  unlinked: boolean;
  /**
   * The site's connection at the provider: `removed`, the provider confirmed it ended (Toss); `pending`, recorded and
   * being removed by the server; `none`, nothing was asked of the provider, which may still list the site (Kakao and
   * Naver, and Toss while its removal is off).
   */
  upstream: 'removed' | 'pending' | 'none';
}

/**
 * Before a payment's details are lost to a sign-in (a redirect to Kakao or Naver, an emailed link): an opaque handle on
 * them, valid 10 minutes, usable only by the order's buyer. Callable without signing in.
 */
export type PreparePaymentResumeRequest = PaymentDetails;
export interface PreparePaymentResumeResponse {
  resumeId: string;
}

/** confirm: attach the first email to the account; change: replace it (docs/plans/SSO-login.md §6). */
export type EmailActionKind = 'confirm' | 'change';
export interface BeginEmailActionRequest {
  kind: EmailActionKind;
  email: string;
  /** Where the browser goes once the link is done. */
  returnTarget: ReturnTarget;
}
export interface BeginEmailActionResponse {
  actionId: string;
  /** ISO 8601. */
  expiresAt: string;
}
export interface InspectEmailActionRequest {
  actionId: string;
}
export interface InspectEmailActionResponse {
  kind: EmailActionKind;
  email: string;
  returnTarget: ReturnTarget;
  completed: boolean;
}
/** With an actionId: completes that action. Without: a fresh email-link sign-in confirms its own address. */
export interface ConfirmEmailRequest {
  actionId?: string;
  /**
   * confirm: the one-time code of the link Firebase emailed (its oobCode). The server attaches the address with it
   * (AUTH_WEB_API_KEY), rather than the browser; without the key it answers EMAIL_LINK_IN_BROWSER first.
   */
  oobCode?: string;
}
export interface ConfirmEmailResponse {
  email: string;
}

export interface SignInWithProviderResponse {
  /** signin and reauth: sign the browser in with it (signInWithCustomToken). A link keeps the current session. */
  customToken?: string;
  uid: string;
  /** The first sign-in with this provider account created this site's account. */
  isNewUser: boolean;
  /** The provider account was attached to the caller's account. */
  linked: boolean;
  /** What the provider said about the person, for display and to prefill an email (never authority). */
  profile: { email: string | null; displayName: string | null };
}

// ---- Firestore documents (plan Appendix C). Clients may read their own users/orders/purchases; only Functions write. ----

/** Structural type met by both the Admin SDK's and the web SDK's Timestamp. */
export interface TimestampLike {
  readonly seconds: number;
  readonly nanoseconds: number;
  toDate(): Date;
  toMillis(): number;
}

/**
 * users/{uid}.entitlements: the account's Remove Ads, written only by the server from the purchases (the authority; it
 * can be rebuilt from them). Read it with entitlementActive: `adFree` alone means nothing (a legacy one has no end).
 */
export interface Entitlements {
  /** RECORD_SCHEMA_VERSION; absent on the earlier offer's records. */
  schemaVersion?: number;
  adFree: boolean;
  /** The period in force or last in force: its order's startsAt and expiresAt (exclusive). */
  adFreeSince?: TimestampLike | null;
  adFreeUntil?: TimestampLike | null;
  adFreeOrderId?: string | null;
  updatedAt?: TimestampLike;
}

/** The Toss keys an order was made for and a payment was approved with: test keys never charge anyone. */
export type PaymentEnvironment = 'test' | 'live';

/**
 * users/{uid}.checkout: the order whose payment confirmPayment last asked Toss to approve. An account approves one
 * payment at a time: another order waits until this one is settled, or until its approval has had time to finish and
 * Toss says it was never approved.
 */
export interface CheckoutReservation {
  orderId: string;
  /** The payment sent to Toss for approval (the order's Idempotency-Key makes it the only one). */
  paymentKey: string;
  currency: Currency;
  /** When confirmPayment last started asking Toss. */
  reservedAt: TimestampLike;
}

/** users/{uid} */
export interface UserDoc {
  /**
   * For display and to prefill the email to confirm: the address a sign-in method reported (a Kakao or Naver
   * address is only a suggestion), or the confirmed one. Never authority over the account.
   */
  email: string | null;
  displayName: string | null;
  createdAt: TimestampLike;
  lastSeenAt: TimestampLike;
  /** Random, opaque Toss customerKey, made by the first createOrder. */
  tossCustomerKey?: string;
  entitlements: Entitlements;
  checkout?: CheckoutReservation;
  /**
   * Display flags of the brokered methods linked to the account (Google comes from Firebase Auth's providerData).
   * Written by the server; the identities collection and Firebase Auth are the authority.
   */
  signInMethods?: Partial<Record<Provider, true>>;
  /**
   * The receipt and recovery email: proven by a link Firebase emailed there, and attached to the account as its
   * passwordless email sign-in. Canonical (lower-case). createOrder refuses an account without one.
   */
  confirmedEmail?: string | null;
  confirmedAt?: TimestampLike | null;
  /** The email action waiting for its link to be opened (only the latest one counts). */
  activeEmailActionId?: string | null;
  /**
   * When the account's sessions were last ended (accountSessions/{uid} decides; this copy makes the pages that read
   * the profile live notice at once).
   */
  sessionsRevokedAt?: TimestampLike | null;
}

/**
 * identities/{identityKey(provider, subject)}: which account a Kakao/Naver/Toss account signs in to (server only).
 * `provisioning` reserves the uid while its Firebase Auth user is created; `active` once the account exists. A
 * mapping is never moved to another uid; deleting the account deletes it.
 */
export interface IdentityDoc {
  uid: string;
  provider: BrokeredProvider;
  /** The provider's id for the person (Kakao 회원번호, Naver id), unchanged. */
  providerUserId: string;
  status: 'provisioning' | 'active';
  /** As the provider last reported them: display and prefill only. */
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  createdAt: TimestampLike;
  lastSignInAt: TimestampLike;
  /**
   * The person disconnected this site at the provider (Naver's or Toss's notification); a sign-in that started after it
   * (a new consent) clears it.
   */
  disconnectedAt?: TimestampLike | null;
  /**
   * The key of identitySessions/ that the sessions signed in with this mapping are bound to (the custom token's claim
   * `sid`); made when the mapping first signs in or is linked, deleted with the mapping.
   */
  sessionKey?: string;
}

/**
 * accountCredentials/{uid}: the Google account and the email address the server approved for this account (server
 * only; the rules read it). Firebase lets any session of the account attach a Google or email credential, or set a
 * password, directly (from any page or script), also a session the site has ended, and some of these make that session
 * a new sign-in. So once this exists, a session counts only if the server made it (a custom token: the broker's, or the
 * server's own email sign-in, carrying `sgen`) or signed in with the approved Google account; an email or password
 * sign-in Firebase made counts for nothing. It is made, from the credentials the account has at that moment, by the
 * first event that ends sessions (Toss's disconnect, removing a method), and changes only through the server: the
 * Google link's begin and approval (beginGoogleLink, approveGoogleSignIn), removing Google, and the email's transition
 * (emailTransitions/{uid}: a confirm or a change a session that still counts began, once Firebase has the address).
 * An account without it has had no session ended, and its sessions count as before. Deleted when the account's
 * deletion completes.
 */
export interface AccountCredentialsDoc {
  /** The Google account (its Firebase provider uid, the token's firebase.identities['google.com'][0]). */
  google: string | null;
  /**
   * Google sign-ins count only after this (auth_time, in whole seconds, strictly after it): set to the time Google was
   * last removed, never earlier, and kept when a Google account is approved again, so a session from before a removal
   * doesn't come back with the same Google account. Absent or null: no Google account was removed since the record.
   */
  googleValidAfter?: TimestampLike | null;
  /** The email address, canonical: the confirmed receipt and recovery email. */
  email: string | null;
  /** A Google link a current session began (beginGoogleLink), until its approval or `until`. */
  pendingGoogle?: PendingGoogleLink | null;
  createdAt: TimestampLike;
  updatedAt: TimestampLike;
}

/** A Google link waiting for its approval (accountCredentials/{uid}.pendingGoogle). */
export interface PendingGoogleLink {
  /** sha256 (hex) of the nonce the begin answered with: the approval brings the nonce itself. */
  nonceHash: string;
  until: TimestampLike;
  /** The session that began it, which must still count when the approval comes. */
  by: SessionSnapshot;
}

/** What decides whether a session counts (sessions.ts), as stored: no field is undefined. */
export interface SessionSnapshot {
  custom?: boolean;
  signInProvider?: string | null;
  authTime?: number;
  sessionGeneration?: number;
  sessionKey?: string;
  googleSub?: string;
  email?: string | null;
}

/**
 * identitySessions/{sessionKey}: the sessions signed in with one provider mapping count only while this exists for the
 * same account (server only; the rules read it). Deleted with its mapping (removing the method, deleting the account),
 * which ends those sessions, and tokens minted for them that weren't redeemed yet.
 */
export interface IdentitySessionDoc {
  uid: string;
  provider: BrokeredProvider;
  createdAt: TimestampLike;
}

/**
 * disconnectNotices/{identityKey}: Toss said this person disconnected the site, also before any account of theirs exists
 * (server only). For 15 minutes after it that Toss account can't sign in, be linked or sign in again
 * (PROVIDER_RECENTLY_DISCONNECTED): Toss's authorization codes last 10 minutes, so whatever Toss does with one issued
 * before the disconnect, it can't be used. It expires an hour after it came (`expiresAt`), and Firestore's TTL deletes it
 * some time after that: TTL deletion runs asynchronously, typically within 24 hours of the expiry, and the notice stays
 * readable until then. The wait counts from `disconnectedAt`, never from `expiresAt`, so it doesn't matter when.
 */
export interface DisconnectNoticeDoc {
  provider: BrokeredProvider;
  disconnectedAt: TimestampLike;
  expiresAt: TimestampLike;
}

/**
 * accountSessions/{uid}: the account's sign-ins from before `validAfter` no longer count (server only; the rules read
 * it, clients can't). Written when Toss says its person disconnected the site, which ends every session of the account.
 * A session counts while its sign-in (the ID token's auth_time, whole seconds) is after `validAfter`, and, for a
 * brokered sign-in, while the custom token it came from carried this `generation` or a later one (claim `sgen`; a token
 * without it counts as 0), so a token minted before the revocation and redeemed after it doesn't count either. Deleted
 * when the account's deletion completes, never before its records are gone.
 */
export interface AccountSessionDoc {
  /** Raised by each revocation; custom tokens carry the value they were minted under. */
  generation: number;
  validAfter: TimestampLike;
  updatedAt: TimestampLike;
}

/**
 * providerDisconnectJobs/{identityKey}: the site's connection with a Toss account, to be ended at Toss (server only).
 * Recorded before the mapping is deleted (unlinking Toss, or deleting the account) and deleted once Toss confirms;
 * while it exists, that Toss account can't sign in or be linked (PROVIDER_REMOVAL_PENDING), so a new consent can't
 * meet a removal of the old one. Holds the provider's id for the person and the bookkeeping, nothing else.
 */
export interface ProviderDisconnectJobDoc {
  provider: BrokeredProvider;
  /** The provider's id for the person (Toss's userKey, as its digits). */
  providerUserId: string;
  /** What ended the connection here. */
  reason: 'unlink' | 'deletion';
  /** `pending`: retried with backoff; `stalled`: needs attention (a permission or configuration error, or failing long). */
  status: 'pending' | 'stalled';
  createdAt: TimestampLike;
  updatedAt: TimestampLike;
  attempts: number;
  /** The run that has the job now, until `expiresAt`. */
  lease: { holder: string; expiresAt: TimestampLike } | null;
  /** From then on the reconciler runs it again (unless a run has it). */
  nextAttemptAt: TimestampLike;
  /** The last failed attempt: an error code, never a message or a body. */
  lastError: { code: string; at: TimestampLike } | null;
}

export interface Failure {
  code: string;
  message: string;
}

/**
 * orders/{orderId}. An order exists only if the buyer accepted the terms and confirmed their age (createOrder
 * refuses otherwise), so `createdAt` is also when they agreed.
 */
export interface OrderDoc {
  /** Removed when the account is deleted (then `uidHash` is set). */
  uid?: string;
  uidHash?: string;
  product: ProductId;
  /** In `currency`: won, or dollars with at most 2 decimals (Toss's units). */
  amount: number;
  currency: Currency;
  status: OrderStatus;
  createdAt: TimestampLike;
  updatedAt: TimestampLike;
  paymentKey?: string;
  failure?: Failure;
  /** The buyer's confirmed email when the order was made: Toss's customerEmail. Replaced by its hash on deletion. */
  receiptEmail?: string;
  receiptEmailHash?: string;
  /** The uid hash of the account that made the order (it never changes, also after a transfer). */
  receiptOwnerUidHash?: string;
  /** Counts moves of the Remove Ads entitlement to another account (admin transfer); 0 at creation. */
  ownershipVersion?: number;
  // The offer the buyer accepted, frozen at creation (RECORD_SCHEMA_VERSION 2; absent on the earlier offer's orders,
  // which can no longer be paid).
  schemaVersion?: number;
  /** REMOVE_ADS_OFFER.productVersion when the order was made. */
  productVersion?: string;
  durationDays?: number;
  termTimeZone?: string;
  /** The price as an integer of the currency's minor unit: won, or cents. `amount` stays in Toss's units. */
  amountMinor?: number;
  /** The versions of the terms and the refund policy the buyer accepted (checked against the current ones). */
  termsVersion?: string;
  refundPolicyVersion?: string;
  /** When the server took the acceptance (the order's creation). */
  acceptedAt?: TimestampLike;
  /** The order can be paid until then (CHECKOUT_WINDOW_MS after creation): the checkout window, not the period. */
  checkoutExpiresAt?: TimestampLike;
  /** The server's Toss keys when the order was made (TOSS_PAYMENT_ENVIRONMENT). */
  paymentEnvironment?: PaymentEnvironment;
}

/** A trimmed copy of the Toss Payment object: whitelisted fields only, nothing that identifies the buyer. */
export interface PaymentRecord {
  paymentKey: string;
  orderId: string;
  orderName: string | null;
  status: string;
  type: string | null;
  method: string | null;
  currency: string | null;
  totalAmount: number;
  balanceAmount: number | null;
  suppliedAmount: number | null;
  vat: number | null;
  requestedAt: string | null;
  approvedAt: string | null;
  lastTransactionKey: string | null;
  receipt: { url: string | null } | null;
  card: {
    issuerCode: string | null;
    acquirerCode: string | null;
    cardType: string | null;
    ownerType: string | null;
    installmentPlanMonths: number | null;
    isInterestFree: boolean | null;
    approveNo: string | null;
    acquireStatus: string | null;
    amount: number | null;
  } | null;
  easyPay: { provider: string | null; amount: number | null; discountAmount: number | null } | null;
  cancels: {
    transactionKey: string | null;
    cancelAmount: number | null;
    cancelReason: string | null;
    canceledAt: string | null;
    cancelStatus: string | null;
    refundableAmount: number | null;
  }[];
  failure: Failure | null;
}

/** purchases/{orderId}: kept 5 years (전자상거래법), pseudonymised when the account is deleted. */
export interface PurchaseDoc {
  /** Removed when the account is deleted (then `uidHash` is set). */
  uid?: string;
  uidHash?: string;
  product: ProductId;
  amount: number;
  currency: Currency;
  paymentKey: string;
  method: string | null;
  approvedAt: TimestampLike;
  receiptUrl: string | null;
  status: PurchaseStatus;
  canceledAt?: TimestampLike | null;
  raw: PaymentRecord;
  /** Copied from the order and kept on every update from Toss (see OrderDoc). */
  receiptEmail?: string;
  receiptEmailHash?: string;
  receiptOwnerUidHash?: string;
  ownershipVersion?: number;
  // The period the payment bought (RECORD_SCHEMA_VERSION 2), worked out once, when Toss's approval is first recorded,
  // and never again: a repeated confirmation, a webhook, a reconciliation, a partial refund, signing in elsewhere or a
  // transfer keep them. A purchase of the earlier offer has none of these, and grants nothing under this offer.
  schemaVersion?: number;
  productVersion?: string;
  durationDays?: number;
  termTimeZone?: string;
  /** Toss's approvedAt, as verified with Toss (never a retry's clock). */
  startsAt?: TimestampLike;
  /** termEnd(startsAt): exclusive. The purchase may stay DONE after it (payment and period are separate). */
  expiresAt?: TimestampLike;
  termsVersion?: string;
  refundPolicyVersion?: string;
  /** The keys the payment was approved with: from the secret key of the order's currency. */
  paymentEnvironment?: PaymentEnvironment;
}

/**
 * webhookEvents/{id}: log of Toss webhooks, one document per (event type, payment, payment state) (server only). A
 * repeated delivery is reconciled again (which changes nothing unless the records disagree with Toss) and counted.
 */
export interface WebhookEventDoc {
  receivedAt: TimestampLike;
  eventType: string;
  paymentKey: string;
  orderId: string | null;
  processedAt: TimestampLike;
  /** The result of the latest delivery. */
  result: string;
  deliveries: number;
}

/**
 * accountDeletions/{uidHash}: written before anything else when an account is deleted, and kept. Holds only the
 * pseudonym (the document id) and times. Every handler that writes a profile, order or purchase for a uid reads it in
 * the same transaction, so nothing is written under the uid once deletion has started (server only).
 */
export interface AccountDeletionDoc {
  startedAt: TimestampLike;
  /** Set when every step finished. Until then the deletion's job (accountDeletionJobs/) finishes it on the server. */
  completedAt: TimestampLike | null;
}

/**
 * accountDeletionJobs/{uidHash}: the work left of a deletion whose marker (same id) exists, written in the marker's
 * transaction and deleted in the one that records its completion (server only, never read by clients). It holds the
 * uid only while the deletion runs: the marker's hash can't be turned back into it. No TTL: an unfinished job stays
 * until it is done.
 */
export interface AccountDeletionJobDoc {
  /** The account being deleted, from the verified sign-in that started the deletion. */
  uid: string;
  createdAt: TimestampLike;
  updatedAt: TimestampLike;
  /** Runs of the steps so far (the callable's, then each delivery's). */
  attempts: number;
  /** The run that has the job now, until `expiresAt` (claimed in a transaction): another run waits for it. */
  lease: { holder: string; expiresAt: TimestampLike } | null;
  /** Deliveries asked of the queue: each one is a task named after the job and its number. */
  dispatches: number;
  /** When the queue last accepted a delivery; null until it has. */
  dispatchedAt: TimestampLike | null;
  /** From then on the reconciler (reconcileAccountDeletions) delivers the job again unless it is running or done. */
  nextCheckAt: TimestampLike;
  /** The last failed run: the step it stopped at and an error code, never a message. */
  lastError: { step: string; code: string; at: TimestampLike } | null;
  /** The last delivery the queue refused: its error code. */
  lastDispatchError?: { code: string; at: TimestampLike } | null;
}

/** The payload of a runAccountDeletion task: the job's id, never a uid. */
export interface AccountDeletionTask {
  jobId: string;
}

/**
 * emailActions/{id}: an email confirmation or change, bound to the account that asked for it, its address and where to
 * return (server only). Expires after 30 minutes (Firestore TTL deletes it later); `completedAt` once done.
 */
export interface EmailActionDoc {
  uid: string;
  kind: EmailActionKind;
  /** Canonical. */
  email: string;
  returnTarget: ReturnTarget;
  createdAt: TimestampLike;
  expiresAt: TimestampLike;
  completedAt: TimestampLike | null;
}

/**
 * emailTransitions/{uid}: the account's latest email confirm or change (server only, no TTL; deleted with the account).
 * The email action expires after 30 minutes and Firestore's TTL deletes it later, but Firebase may apply a change on
 * its own page while the browser never comes back: this keeps what the server needs to approve the address then. The
 * address is approved (the account's confirmed email, and its approved one when it has a credential record) only once
 * Firebase has it as the account's verified email, until `until`, and while the session that began it still counts:
 * one a disconnect, a removed mapping or a removed Google account ended since can't finish it. The callables approve
 * it when they meet it (signing in with the address, the browser's return), and reconcileEmailTransitions does every
 * few minutes. A newer begin replaces it.
 */
export interface EmailTransitionDoc {
  /** The action it began with (emailActions/{actionId}). */
  actionId: string;
  kind: EmailActionKind;
  /** The address it leads to, canonical. */
  email: string;
  returnTarget: ReturnTarget;
  /** The session that began it, as it counted then (removed once the transition is done). */
  by: SessionSnapshot | null;
  createdAt: TimestampLike;
  /** The action's end, plus EMAIL_TRANSITION_GRACE_MS (a change applied in time is recorded by then). */
  until: TimestampLike;
  /** Done: approved, or closed without approving (`result`). */
  completedAt: TimestampLike | null;
  result: 'approved' | 'expired' | 'revoked' | null;
  /**
   * When reconcileEmailTransitions looks at it next: as it begins, then the time of each look that found it waiting,
   * so a run takes the transitions looked at longest ago first; null once it is done.
   */
  nextCheckAt: TimestampLike | null;
}

/**
 * paymentResumes/{id}: Toss's success parameters kept while the buyer signs in (server only), for at most 10 minutes.
 * Only the order's buyer can use it; it approves nothing by itself.
 */
export interface PaymentResumeDoc {
  uid: string;
  orderId: string;
  paymentKey: string;
  amount: number;
  createdAt: TimestampLike;
  expiresAt: TimestampLike;
  completedAt: TimestampLike | null;
}

/**
 * adminActions/{operationId}: what an administrator did to an order (server only, kept like the order): who (hashes
 * only), the request's digest, and the result. Readable only through adminLookupOrder.
 */
export interface AdminActionDoc {
  at: TimestampLike;
  byUidHash: string;
  action: 'transfer' | 'cancel';
  orderId: string;
  /** sha256 of the request's canonical form: a retry with other arguments under the same id is refused. */
  requestDigest: string;
  fromUidHash: string | null;
  toUidHash: string | null;
  expectedVersion: number | null;
  resultingVersion: number | null;
  outcome: string;
  /** The reason's length: the support case itself stays with the owner, not here. */
  reasonLength: number;
}
